import { useMemo } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { SearchableSelect } from '@/components/common/searchable-select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  collectVersions,
  type OnFarmImplantFreezingEmbryo,
  type OnFarmImplantFreezingFieldChanges,
  OnFarmImplantRequestError,
  useUpdateOnFarmImplantFreezingFields,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';
import { parseWallClock, todayWallClockDate, toWallClockISO } from '@/utils';

import { FREEZING_TEXT_LIMITS } from './columnDefs';
import type { FreezingFieldOptions } from './useFreezingFieldOptions';

type ApplyFreezeField =
  | 'freezeDate'
  | 'freezeTechId'
  | 'freezeMethodId'
  | 'dtPlus'
  | 'cane'
  | 'storageLocationId'
  | 'freezer'
  | 'graderId'
  | 'accountId'
  | 'crackedZona';

type ApplyFreezeFormValues = Record<ApplyFreezeField, string> & {
  // Only checked fields are saved, so a checked field left blank clears it.
  apply: Record<ApplyFreezeField, boolean>;
};

const DEFAULT_VALUES: ApplyFreezeFormValues = {
  freezeDate: '',
  freezeTechId: '',
  freezeMethodId: '',
  dtPlus: '',
  cane: '',
  storageLocationId: '',
  freezer: '',
  graderId: '',
  accountId: '',
  crackedZona: '',
  apply: {
    freezeDate: false,
    freezeTechId: false,
    freezeMethodId: false,
    dtPlus: false,
    cane: false,
    storageLocationId: false,
    freezer: false,
    graderId: false,
    accountId: false,
    crackedZona: false,
  },
};

const textValue = (value: string) => value.trim() || null;
const yesNoValue = (value: string) => (value === '' ? null : value === 'true');
const dateValue = (value: string) => {
  const date = value ? parseWallClock(value) : null;
  return date ? toWallClockISO(date) : null;
};

type ApplyFreezeDataDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantFreezingEmbryo[];
  options: FreezingFieldOptions;
};

export const ApplyFreezeDataDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
  options,
}: ApplyFreezeDataDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutateAsync: updateFields } = useUpdateOnFarmImplantFreezingFields();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ApplyFreezeFormValues>({ defaultValues: DEFAULT_VALUES });
  const [freezeDate, apply] = useWatch({
    control,
    name: ['freezeDate', 'apply'],
  });

  const today = todayWallClockDate();
  const hasApplied = Object.values(apply).some(Boolean);
  const futureDate =
    apply.freezeDate && freezeDate !== '' && freezeDate > today;

  const yesNoItems = useMemo(
    () => [
      { value: 'true', label: t('onFarmImplant.onFarmFreezing.yes') },
      { value: 'false', label: t('onFarmImplant.onFarmFreezing.no') },
    ],
    [t],
  );

  // Filling in a field checks it.
  const check = (name: ApplyFreezeField) => setValue(`apply.${name}`, true);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const onSubmit = async (values: ApplyFreezeFormValues) => {
    const { apply } = values;
    const changes: OnFarmImplantFreezingFieldChanges = {};
    if (apply.freezeDate)
      changes.freezeDate = { value: dateValue(values.freezeDate) };
    if (apply.freezeTechId)
      changes.freezeTechId = { value: textValue(values.freezeTechId) };
    if (apply.freezeMethodId)
      changes.freezeMethodId = { value: textValue(values.freezeMethodId) };
    if (apply.dtPlus) changes.dtPlus = { value: yesNoValue(values.dtPlus) };
    if (apply.cane) changes.cane = { value: textValue(values.cane) };
    if (apply.storageLocationId)
      changes.storageLocationId = {
        value: textValue(values.storageLocationId),
      };
    if (apply.freezer) changes.freezer = { value: textValue(values.freezer) };
    if (apply.graderId)
      changes.graderId = { value: textValue(values.graderId) };
    if (apply.accountId)
      changes.accountId = { value: textValue(values.accountId) };
    if (apply.crackedZona)
      changes.crackedZona = { value: yesNoValue(values.crackedZona) };

    try {
      const result = await updateFields({
        sessionId,
        input: {
          sessionEmbryoIds: rows.map((row) => row.sessionEmbryoId),
          changes,
          versions: collectVersions(rows),
        },
      });

      handleOpenChange(false);
      toast.show({
        severity: 'success',
        summary: t('onFarmImplant.onFarmFreezing.apply.success', {
          count: result.items.length,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setError('root.server', { message: error.problems.join(' ') });
        return;
      }

      console.error('Failed to apply freeze data:', error);
      toast.show({
        severity: 'error',
        summary: t('onFarmImplant.onFarmFreezing.apply.failed'),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.onFarmFreezing.apply.title', {
              count: rows.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.onFarmFreezing.apply.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {errors.root?.server && (
            <p
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              {errors.root.server.message}
            </p>
          )}

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.freezeDate"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t(
                        'onFarmImplant.onFarmFreezing.fields.freezeDate',
                      ),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-freezeDate">
              {t('onFarmImplant.onFarmFreezing.fields.freezeDate')}
            </Label>
            <Input
              id="apply-freeze-freezeDate"
              type="date"
              max={today}
              className="h-8 text-xs"
              {...register('freezeDate', {
                onChange: () => check('freezeDate'),
              })}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.freezeTechId"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t(
                        'onFarmImplant.onFarmFreezing.fields.freezeTech',
                      ),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-freezeTechId">
              {t('onFarmImplant.onFarmFreezing.fields.freezeTech')}
            </Label>
            <Controller
              name="freezeTechId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  className="h-8 w-full text-xs"
                  showClear
                  options={options.techOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    check('freezeTechId');
                  }}
                  placeholder={t('onFarmImplant.onFarmFreezing.apply.select')}
                  emptyMessage={t(
                    'onFarmImplant.onFarmFreezing.apply.noOptions',
                  )}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.freezeMethodId"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t(
                        'onFarmImplant.onFarmFreezing.fields.freezeMethod',
                      ),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-freezeMethodId">
              {t('onFarmImplant.onFarmFreezing.fields.freezeMethod')}
            </Label>
            <Controller
              name="freezeMethodId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  className="h-8 w-full text-xs"
                  showClear
                  options={options.freezeMethodOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    check('freezeMethodId');
                  }}
                  placeholder={t('onFarmImplant.onFarmFreezing.apply.select')}
                  emptyMessage={t(
                    'onFarmImplant.onFarmFreezing.apply.noOptions',
                  )}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.dtPlus"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t('onFarmImplant.onFarmFreezing.fields.dtPlus'),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-dtPlus">
              {t('onFarmImplant.onFarmFreezing.fields.dtPlus')}
            </Label>
            <Controller
              name="dtPlus"
              control={control}
              render={({ field }) => (
                <Select
                  items={yesNoItems}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange((value as string | null) ?? '');
                    check('dtPlus');
                  }}
                >
                  <SelectTrigger
                    id="apply-freeze-dtPlus"
                    className="h-8 w-full text-xs"
                  >
                    <SelectValue
                      placeholder={t(
                        'onFarmImplant.onFarmFreezing.apply.select',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {yesNoItems.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.cane"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t('onFarmImplant.onFarmFreezing.fields.cane'),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-cane">
              {t('onFarmImplant.onFarmFreezing.fields.cane')}
            </Label>
            <Input
              id="apply-freeze-cane"
              maxLength={FREEZING_TEXT_LIMITS.cane}
              className="h-8 text-xs"
              {...register('cane', { onChange: () => check('cane') })}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.storageLocationId"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t(
                        'onFarmImplant.onFarmFreezing.fields.storageLocation',
                      ),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-storageLocationId">
              {t('onFarmImplant.onFarmFreezing.fields.storageLocation')}
            </Label>
            <Controller
              name="storageLocationId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  className="h-8 w-full text-xs"
                  showClear
                  options={options.storageLocationOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    check('storageLocationId');
                  }}
                  placeholder={t('onFarmImplant.onFarmFreezing.apply.select')}
                  emptyMessage={t(
                    'onFarmImplant.onFarmFreezing.apply.noOptions',
                  )}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.freezer"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t('onFarmImplant.onFarmFreezing.fields.freezer'),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-freezer">
              {t('onFarmImplant.onFarmFreezing.fields.freezer')}
            </Label>
            <Input
              id="apply-freeze-freezer"
              maxLength={FREEZING_TEXT_LIMITS.freezer}
              className="h-8 text-xs"
              {...register('freezer', { onChange: () => check('freezer') })}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.graderId"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t('onFarmImplant.onFarmFreezing.fields.grader'),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-graderId">
              {t('onFarmImplant.onFarmFreezing.fields.grader')}
            </Label>
            <Controller
              name="graderId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  className="h-8 w-full text-xs"
                  showClear
                  options={options.techOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    check('graderId');
                  }}
                  placeholder={t('onFarmImplant.onFarmFreezing.apply.select')}
                  emptyMessage={t(
                    'onFarmImplant.onFarmFreezing.apply.noOptions',
                  )}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.accountId"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t('onFarmImplant.onFarmFreezing.fields.account'),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-accountId">
              {t('onFarmImplant.onFarmFreezing.fields.account')}
            </Label>
            <Controller
              name="accountId"
              control={control}
              render={({ field }) => (
                <AccountsCombobox
                  className="h-8 w-full text-xs"
                  value={field.value || undefined}
                  onChange={(accountId) => {
                    field.onChange(accountId ?? '');
                    check('accountId');
                  }}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3">
            <Controller
              name="apply.crackedZona"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label={t(
                    'onFarmImplant.onFarmFreezing.apply.applyField',
                    {
                      field: t(
                        'onFarmImplant.onFarmFreezing.fields.crackedZona',
                      ),
                    },
                  )}
                />
              )}
            />
            <Label className="text-xs" htmlFor="apply-freeze-crackedZona">
              {t('onFarmImplant.onFarmFreezing.fields.crackedZona')}
            </Label>
            <Controller
              name="crackedZona"
              control={control}
              render={({ field }) => (
                <Select
                  items={yesNoItems}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange((value as string | null) ?? '');
                    check('crackedZona');
                  }}
                >
                  <SelectTrigger
                    id="apply-freeze-crackedZona"
                    className="h-8 w-full text-xs"
                  >
                    <SelectValue
                      placeholder={t(
                        'onFarmImplant.onFarmFreezing.apply.select',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {yesNoItems.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {futureDate && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.onFarmFreezing.apply.futureDate')}
            </p>
          )}

          <p className="text-sm font-medium text-gray-700">
            {t('onFarmImplant.onFarmFreezing.apply.recordCount', {
              count: rows.length,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={
              rows.length === 0 || !hasApplied || futureDate || isSubmitting
            }
          >
            {isSubmitting && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.onFarmFreezing.apply.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
